/**
 * A minimal, general-purpose `CanvasRenderingContext2D` recorder that emits SVG
 * instead of rasterising. It implements the subset of the 2D canvas API used by
 * the block diagram's `HtmlCanvasVisual`s (paths, text, images, transforms,
 * clipping) so their existing `render(ctx)` methods produce true vector SVG.
 *
 * Each drawing operation is emitted as an SVG element carrying the current
 * transform as a `transform="matrix(...)"` attribute; coordinates are recorded
 * in local user space. This is accurate as long as the transform is stable
 * within a single path (true for these visuals).
 *
 * Not a complete canvas implementation — only what the block diagram needs.
 */

import { logWarn } from '@ardoq/logging';
import { isTruthy } from '@ardoq/common-helpers';

/** [a, b, c, d, e, f] affine matrix, matching CanvasRenderingContext2D. */
type Matrix = readonly [number, number, number, number, number, number];

const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0];

const multiply = (m: Matrix, n: Matrix): Matrix => [
  m[0] * n[0] + m[2] * n[1],
  m[1] * n[0] + m[3] * n[1],
  m[0] * n[2] + m[2] * n[3],
  m[1] * n[2] + m[3] * n[3],
  m[0] * n[4] + m[2] * n[5] + m[4],
  m[1] * n[4] + m[3] * n[5] + m[5],
];

const isIdentity = (m: Matrix): boolean =>
  m[0] === 1 &&
  m[1] === 0 &&
  m[2] === 0 &&
  m[3] === 1 &&
  m[4] === 0 &&
  m[5] === 0;

/** round to 3 decimals to keep the SVG small */
const fmt = (value: number): string =>
  Number.isFinite(value) ? String(Math.round(value * 1000) / 1000) : '0';

const matrixToString = (m: Matrix): string => `matrix(${m.map(fmt).join(' ')})`;

export const escapeXmlText = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const escapeXmlAttr = (value: string): string =>
  escapeXmlText(value).replace(/"/g, '&quot;');

const SVG_NS = 'http://www.w3.org/2000/svg';

/** attributes describing a nested `<svg>`'s own viewport, dropped when flattening it to a `<g>` */
const NESTED_SVG_LAYOUT_ATTRS = new Set([
  'x',
  'y',
  'width',
  'height',
  'viewBox',
  'preserveAspectRatio',
  'transform',
  'id',
  'xmlns',
  'xmlns:xlink',
  'version',
]);

/** presentation attributes a referenced icon's root `<svg>` sets for its children to inherit */
const INHERITABLE_PAINT_ATTRS = [
  'color',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-linecap',
  'stroke-linejoin',
  'stroke-dasharray',
  'stroke-opacity',
  'fill-opacity',
  'fill-rule',
  'opacity',
  'font-family',
  'font-size',
];

type ViewBox = { x: number; y: number; width: number; height: number };

/** a `<defs>` entry for a drawn image, and how a `<use>` should reference it */
type ImageDef = { defId: string; kind: 'image' | 'symbol' };

const parseLength = (value: string | null): number | undefined => {
  if (value === null) return undefined;
  // Only unitless / px lengths convert directly to user units here; %, em, etc.
  // need viewport or font context this pass lacks, so treat them as absent and
  // let callers fall back to the viewBox size rather than a truncated number
  // (`parseFloat("100%")` → 100).
  if (!/^[+-]?(?:\d*\.\d+|\d+\.?\d*)(?:px)?$/i.test(value.trim())) {
    return undefined;
  }
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
};

const parseViewBox = (value: string | null): ViewBox | undefined => {
  if (!value) return undefined;
  const parts = value
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some(n => !Number.isFinite(n))) {
    return undefined;
  }
  const [x, y, width, height] = parts;
  if (width <= 0 || height <= 0) return undefined;
  return { x, y, width, height };
};

type PreserveAspectRatio = { align: string; meetOrSlice: 'meet' | 'slice' };

const parsePreserveAspectRatio = (
  value: string | null
): PreserveAspectRatio => {
  const tokens = (value ?? 'xMidYMid meet').trim().split(/\s+/);
  const align = tokens[0] === 'defer' ? (tokens[1] ?? 'xMidYMid') : tokens[0];
  const meetOrSlice = value?.includes('slice') ? 'slice' : 'meet';
  return { align: align || 'xMidYMid', meetOrSlice };
};

/**
 * The affine transform equivalent to establishing a nested SVG viewport, per the
 * SVG "equivalent transform of an SVG viewport" algorithm. Applied when a nested
 * `<svg>` is flattened into a `<g>`, so no browser has to reconcile a nested
 * viewport against a stretched (`preserveAspectRatio="none"`) ancestor — the case
 * Chrome and Firefox render differently.
 */
const viewportTransform = (element: Element): string => {
  const x = parseLength(element.getAttribute('x')) ?? 0;
  const y = parseLength(element.getAttribute('y')) ?? 0;
  const viewBox = parseViewBox(element.getAttribute('viewBox'));
  if (!viewBox) {
    return x !== 0 || y !== 0 ? `translate(${fmt(x)} ${fmt(y)})` : '';
  }
  const width = parseLength(element.getAttribute('width')) ?? viewBox.width;
  const height = parseLength(element.getAttribute('height')) ?? viewBox.height;
  const { align, meetOrSlice } = parsePreserveAspectRatio(
    element.getAttribute('preserveAspectRatio')
  );

  let sx = width / viewBox.width;
  let sy = height / viewBox.height;
  if (align !== 'none') {
    const uniform =
      meetOrSlice === 'slice' ? Math.max(sx, sy) : Math.min(sx, sy);
    sx = uniform;
    sy = uniform;
  }
  let tx = x - viewBox.x * sx;
  let ty = y - viewBox.y * sy;
  if (align.includes('xMid')) tx += (width - viewBox.width * sx) / 2;
  else if (align.includes('xMax')) tx += width - viewBox.width * sx;
  if (align.includes('YMid')) ty += (height - viewBox.height * sy) / 2;
  else if (align.includes('YMax')) ty += height - viewBox.height * sy;

  return `matrix(${fmt(sx)} 0 0 ${fmt(sy)} ${fmt(tx)} ${fmt(ty)})`;
};

/** replace every descendant nested `<svg>` with a `<g>` carrying its viewport transform */
const flattenNestedSvgElements = (root: Element, doc: Document): void => {
  for (const nested of Array.from(root.querySelectorAll('svg'))) {
    const group = doc.createElementNS(SVG_NS, 'g');
    for (const attr of Array.from(nested.attributes)) {
      if (!NESTED_SVG_LAYOUT_ATTRS.has(attr.name)) {
        group.setAttribute(attr.name, attr.value);
      }
    }
    const transform = [
      nested.getAttribute('transform'),
      viewportTransform(nested),
    ]
      .filter(isTruthy)
      .join(' ');
    if (transform) group.setAttribute('transform', transform);
    while (nested.firstChild) group.appendChild(nested.firstChild);
    nested.replaceWith(group);
  }
};

/** an SVG root's coordinate box: its `viewBox`, else its `width`/`height` */
const rootViewBox = (root: Element): ViewBox | undefined => {
  const viewBox = parseViewBox(root.getAttribute('viewBox'));
  if (viewBox) return viewBox;
  const width = parseLength(root.getAttribute('width'));
  const height = parseLength(root.getAttribute('height'));
  return width && height ? { x: 0, y: 0, width, height } : undefined;
};

const decodeSvgDataUrl = (href: string): string | null => {
  const match = href.match(/^data:image\/svg\+xml([^,]*),(.*)$/s);
  if (!match) return null;
  const [, params, data] = match;
  try {
    // `atob` yields a Latin-1 byte string; decode those bytes as UTF-8 so any
    // non-ASCII SVG content survives (`decodeURIComponent` is already UTF-8).
    return params.includes('base64')
      ? new TextDecoder().decode(
          Uint8Array.from(atob(data), c => c.charCodeAt(0))
        )
      : decodeURIComponent(data);
  } catch {
    return null;
  }
};

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Prefix every `id` defined inside an inlined icon and rewrite the icon's own
 * references to them. Inlining lifts the icon into the shared export document,
 * whose `id` namespace is global — a `<symbol>` does not scope its descendants'
 * ids — so without this two icons that reuse an internal id (e.g. the
 * `paint0_linear` / `clip0_…` names design tools emit) would cross-reference
 * whichever def serialized first. Rewrites every `#id` reference: `href="#id"`,
 * `url(#id)` / `url('#id')` (quoted or not) in attributes, and the same inside a
 * `<style>` element (CSS `url(...)` and `#id` selectors).
 */
const namespaceInternalIds = (scope: Element, prefix: string): void => {
  const rename = (id: string): string => `${prefix}__${id}`;
  const ids = Array.from(
    new Set(
      Array.from(scope.querySelectorAll('[id]'), el => el.getAttribute('id'))
    )
  ).filter((id): id is string => Boolean(id));
  if (ids.length === 0) return;

  for (const el of scope.querySelectorAll('[id]')) {
    el.setAttribute('id', rename(el.getAttribute('id') ?? ''));
  }

  // Rewrite each `#id` reference token — covers `href="#id"`, `url(#id)`,
  // `url('#id')`, and CSS `#id` selectors — leaving ids that only prefix a longer
  // id untouched via the trailing boundary.
  const rewriteReferences = (text: string): string =>
    ids.reduce(
      (acc, id) =>
        acc.replace(
          new RegExp(`#${escapeRegExp(id)}(?![\\w-])`, 'g'),
          `#${rename(id)}`
        ),
      text
    );

  for (const el of [scope, ...scope.querySelectorAll('*')]) {
    for (const attr of Array.from(el.attributes)) {
      if (!attr.value.includes('#')) continue;
      const rewritten = rewriteReferences(attr.value);
      if (rewritten !== attr.value) el.setAttribute(attr.name, rewritten);
    }
  }
  for (const styleEl of scope.querySelectorAll('style')) {
    const css = styleEl.textContent;
    if (css?.includes('#')) styleEl.textContent = rewriteReferences(css);
  }
};

/**
 * Build a reusable `<symbol>` for an SVG-data-URL image, or `null` to fall back to
 * a raster `<image>`. Inlining the vector markup (rather than burying it in a
 * base64 `<image href>`) makes the export render consistently across browsers and
 * keeps it a true, editable vector.
 */
const buildSvgSymbol = (href: string, id: string): string | null => {
  const svgText = decodeSvgDataUrl(href);
  if (!svgText) return null;
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  const root = doc.documentElement;
  if (
    !root ||
    root.nodeName.toLowerCase() !== 'svg' ||
    doc.querySelector('parsererror')
  ) {
    return null;
  }

  const viewBox = rootViewBox(root);
  if (!viewBox) return null;

  flattenNestedSvgElements(root, doc);

  const symbol = doc.createElementNS(SVG_NS, 'symbol');
  symbol.setAttribute('id', id);
  symbol.setAttribute(
    'viewBox',
    `${fmt(viewBox.x)} ${fmt(viewBox.y)} ${fmt(viewBox.width)} ${fmt(viewBox.height)}`
  );
  // canvas `drawImage` renders the icon at its intrinsic size, fitting its viewBox
  // with the root's own aspect-ratio rule (SVG default `xMidYMid meet`); forcing
  // `none` here would instead stretch shapes to their node border.
  const preserveAspectRatio = root.getAttribute('preserveAspectRatio');
  if (preserveAspectRatio !== null) {
    symbol.setAttribute('preserveAspectRatio', preserveAspectRatio);
  }
  for (const name of INHERITABLE_PAINT_ATTRS) {
    const value = root.getAttribute(name);
    if (value !== null) symbol.setAttribute(name, value);
  }
  while (root.firstChild) symbol.appendChild(root.firstChild);
  namespaceInternalIds(symbol, id);
  return new XMLSerializer().serializeToString(symbol);
};

type ParsedFont = {
  fontSize: string;
  fontFamily: string;
  fontWeight?: string;
  fontStyle?: string;
};

/**
 * Parse a CSS/canvas font shorthand, e.g. `600 12px Inter, sans-serif`.
 *
 * The size may carry a line height - `12px/14px`, `12px/normal`, `12px/1.4` are all legal - which
 * is matched and discarded, being no part of the size.
 */
const parseFont = (font: string): ParsedFont => {
  const sizeMatch = font.match(/(\d*\.?\d+)px(?:\s*\/\s*\S+?)?\s+(.+)$/);
  if (!sizeMatch) {
    return { fontSize: '12', fontFamily: 'sans-serif' };
  }
  const before = font.slice(0, sizeMatch.index).trim().split(/\s+/);
  const fontWeight = before.find(t =>
    /^(bold|bolder|lighter|[1-9]00)$/.test(t)
  );
  const fontStyle = before.find(t => /^(italic|oblique)$/.test(t));
  return {
    fontSize: sizeMatch[1],
    fontFamily: sizeMatch[2],
    fontWeight,
    fontStyle,
  };
};

const TEXT_ANCHOR: Record<string, string> = {
  left: 'start',
  right: 'end',
  center: 'middle',
  start: 'start',
  end: 'end',
};

const DOMINANT_BASELINE: Record<string, string> = {
  top: 'text-before-edge',
  hanging: 'hanging',
  middle: 'central',
  alphabetic: 'alphabetic',
  ideographic: 'ideographic',
  bottom: 'text-after-edge',
};

const asColor = (style: unknown): string =>
  typeof style === 'string' ? style : 'black';

const TWO_PI = Math.PI * 2;

/** build an SVG path segment for an (elliptical) arc, handling the full-circle case. */
const ellipseToPath = (
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  rotation: number,
  startAngle: number,
  endAngle: number,
  counterclockwise: boolean
): string => {
  const point = (angle: number) => {
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    const x = rx * Math.cos(angle);
    const y = ry * Math.sin(angle);
    return [cx + x * cos - y * sin, cy + x * sin + y * cos] as const;
  };

  const delta = endAngle - startAngle;
  const isFull = Math.abs(delta) >= TWO_PI - 1e-6;
  const rot = fmt((rotation * 180) / Math.PI);

  if (isFull) {
    const [sx, sy] = point(startAngle);
    const [mx, my] = point(startAngle + Math.PI);
    return (
      `M${fmt(sx)} ${fmt(sy)}` +
      `A${fmt(rx)} ${fmt(ry)} ${rot} 1 1 ${fmt(mx)} ${fmt(my)}` +
      `A${fmt(rx)} ${fmt(ry)} ${rot} 1 1 ${fmt(sx)} ${fmt(sy)}`
    );
  }

  const [sx, sy] = point(startAngle);
  const [ex, ey] = point(endAngle);
  const sweep = counterclockwise ? 0 : 1;
  const largeArc = Math.abs(delta) > Math.PI ? 1 : 0;
  return `M${fmt(sx)} ${fmt(sy)}A${fmt(rx)} ${fmt(ry)} ${rot} ${largeArc} ${sweep} ${fmt(ex)} ${fmt(ey)}`;
};

type DrawState = {
  transform: Matrix;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  globalAlpha: number;
  font: string;
  textAlign: string;
  textBaseline: string;
  lineCap: string;
  lineJoin: string;
  lineDash: number[];
  lineDashOffset: number;
  clipId: string | null;
};

const initialState = (): DrawState => ({
  transform: IDENTITY,
  fillStyle: '#000000',
  strokeStyle: '#000000',
  lineWidth: 1,
  globalAlpha: 1,
  font: '10px sans-serif',
  textAlign: 'start',
  textBaseline: 'alphabetic',
  lineCap: 'butt',
  lineJoin: 'miter',
  lineDash: [],
  lineDashOffset: 0,
  clipId: null,
});

/**
 * Create a recorder exposing the subset of the 2D canvas API the block diagram
 * uses. State lives in the closure rather than on `this`, and the returned
 * object's own properties (methods + accessors) are what {@link
 * createRecordingContext} routes the canvas API to.
 */
export const createCanvasToSvgContext = () => {
  const stack: DrawState[] = [];
  const elements: string[] = [];
  const defs: string[] = [];
  /** every image referenced via `drawImage`, so callers can await their load */
  const drawnImages = new Set<HTMLImageElement>();
  /** dedupe inlined symbols by `href` alone — a `<symbol>` is size-independent */
  const symbolDefs = new Map<string, ImageDef>();
  /** dedupe raster `<image>` defs by `href|width|height` — size is baked into the def */
  const rasterDefs = new Map<string, ImageDef>();
  /** bounding box of drawn content in root coordinates, for tight sizing */
  const bounds = {
    minX: Infinity,
    minY: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
  };
  /** a real 2D context, only for text measurement */
  const measureContext = document.createElement('canvas').getContext('2d');
  /** reassignable scalars, grouped so the closures mutate a single const binding */
  const mutable = {
    state: initialState(),
    path: '',
    idCounter: 0,
    currentX: 0,
    currentY: 0,
  };

  /** expand the content bounds by a point given in the current user space */
  const mark = (x: number, y: number): void => {
    const m = mutable.state.transform;
    const rx = m[0] * x + m[2] * y + m[4];
    const ry = m[1] * x + m[3] * y + m[5];
    if (rx < bounds.minX) bounds.minX = rx;
    if (ry < bounds.minY) bounds.minY = ry;
    if (rx > bounds.maxX) bounds.maxX = rx;
    if (ry > bounds.maxY) bounds.maxY = ry;
  };

  const commonAttrs = (): string => {
    const { transform, globalAlpha, clipId } = mutable.state;
    const parts: string[] = [];
    if (!isIdentity(transform)) {
      parts.push(`transform="${matrixToString(transform)}"`);
    }
    if (globalAlpha < 1) {
      parts.push(`opacity="${fmt(globalAlpha)}"`);
    }
    if (clipId) {
      parts.push(`clip-path="url(#${clipId})"`);
    }
    return parts.join(' ');
  };

  const strokeAttrs = (): string => {
    const {
      strokeStyle,
      lineWidth,
      lineCap,
      lineJoin,
      lineDash,
      lineDashOffset,
    } = mutable.state;
    const parts = [
      `stroke="${escapeXmlAttr(strokeStyle)}"`,
      `stroke-width="${fmt(lineWidth)}"`,
    ];
    if (lineCap !== 'butt') parts.push(`stroke-linecap="${lineCap}"`);
    if (lineJoin !== 'miter') parts.push(`stroke-linejoin="${lineJoin}"`);
    if (lineDash.length) {
      parts.push(`stroke-dasharray="${lineDash.map(fmt).join(',')}"`);
      if (lineDashOffset) {
        parts.push(`stroke-dashoffset="${fmt(lineDashOffset)}"`);
      }
    }
    return parts.join(' ');
  };

  const lineTo = (x: number, y: number): void => {
    mutable.path += `L${fmt(x)} ${fmt(y)}`;
    mutable.currentX = x;
    mutable.currentY = y;
    mark(x, y);
  };

  const measureText = (text: string): TextMetrics => {
    if (measureContext) {
      measureContext.font = mutable.state.font;
      return measureContext.measureText(text);
    }
    return { width: text.length * 6 } as TextMetrics;
  };

  const fillText = (text: string, x: number, y: number): void => {
    const { fontSize, fontFamily, fontWeight, fontStyle } = parseFont(
      mutable.state.font
    );
    const parts = [
      `x="${fmt(x)}"`,
      `y="${fmt(y)}"`,
      `font-family="${escapeXmlAttr(fontFamily)}"`,
      `font-size="${fontSize}"`,
      `fill="${escapeXmlAttr(mutable.state.fillStyle)}"`,
      `text-anchor="${TEXT_ANCHOR[mutable.state.textAlign] ?? 'start'}"`,
      `dominant-baseline="${DOMINANT_BASELINE[mutable.state.textBaseline] ?? 'alphabetic'}"`,
    ];
    if (fontWeight) parts.push(`font-weight="${fontWeight}"`);
    if (fontStyle) parts.push(`font-style="${fontStyle}"`);
    const size = Number(fontSize) || 12;
    const width = measureText(text).width;
    const anchor = TEXT_ANCHOR[mutable.state.textAlign] ?? 'start';
    const left =
      anchor === 'middle' ? x - width / 2 : anchor === 'end' ? x - width : x;
    mark(left, y - size);
    mark(left + width, y + size);
    elements.push(
      `<text ${parts.join(' ')} ${commonAttrs()}>${escapeXmlText(text)}</text>`
    );
  };

  /**
   * Define an image in `<defs>` once and return how to reference it. SVG icons
   * are inlined as a reusable, size-independent `<symbol>` (deduped by href);
   * anything else (raster, or SVG that fails to parse) stays a base64 `<image>`
   * (deduped by href + size, which the def bakes in).
   */
  const defineImage = (
    href: string,
    width: number,
    height: number
  ): ImageDef => {
    const symbolHit = symbolDefs.get(href);
    if (symbolHit) return symbolHit;
    const rasterKey = `${href}|${fmt(width)}|${fmt(height)}`;
    const rasterHit = rasterDefs.get(rasterKey);
    if (rasterHit) return rasterHit;

    const defId = `cts-img-${mutable.idCounter++}`;
    const symbol = buildSvgSymbol(href, defId);
    if (symbol) {
      defs.push(symbol);
      const def: ImageDef = { defId, kind: 'symbol' };
      symbolDefs.set(href, def);
      return def;
    }
    defs.push(
      `<image id="${defId}" href="${escapeXmlAttr(href)}" width="${fmt(width)}" height="${fmt(height)}" preserveAspectRatio="none"/>`
    );
    const def: ImageDef = { defId, kind: 'image' };
    rasterDefs.set(rasterKey, def);
    return def;
  };

  return {
    // #region state accessors used by the visuals
    get fillStyle(): string {
      return mutable.state.fillStyle;
    },
    set fillStyle(value: string | CanvasGradient | CanvasPattern) {
      mutable.state.fillStyle = asColor(value);
    },
    get strokeStyle(): string {
      return mutable.state.strokeStyle;
    },
    set strokeStyle(value: string | CanvasGradient | CanvasPattern) {
      mutable.state.strokeStyle = asColor(value);
    },
    get lineWidth(): number {
      return mutable.state.lineWidth;
    },
    set lineWidth(value: number) {
      mutable.state.lineWidth = value;
    },
    get globalAlpha(): number {
      return mutable.state.globalAlpha;
    },
    set globalAlpha(value: number) {
      mutable.state.globalAlpha = value;
    },
    get font(): string {
      return mutable.state.font;
    },
    set font(value: string) {
      mutable.state.font = value;
    },
    get textAlign(): string {
      return mutable.state.textAlign;
    },
    set textAlign(value: string) {
      mutable.state.textAlign = value;
    },
    get textBaseline(): string {
      return mutable.state.textBaseline;
    },
    set textBaseline(value: string) {
      mutable.state.textBaseline = value;
    },
    get lineCap(): string {
      return mutable.state.lineCap;
    },
    set lineCap(value: string) {
      mutable.state.lineCap = value;
    },
    get lineJoin(): string {
      return mutable.state.lineJoin;
    },
    set lineJoin(value: string) {
      mutable.state.lineJoin = value;
    },
    // globalCompositeOperation is tracked but not reproduced in SVG
    globalCompositeOperation: 'source-over',
    get lineDashOffset(): number {
      return mutable.state.lineDashOffset;
    },
    set lineDashOffset(value: number) {
      mutable.state.lineDashOffset = value;
    },
    miterLimit: 10,
    /** every image referenced via `drawImage`, so callers can await their load */
    drawnImages,
    // #endregion

    save(): void {
      stack.push({ ...mutable.state, lineDash: [...mutable.state.lineDash] });
    },
    restore(): void {
      const previous = stack.pop();
      if (previous) {
        mutable.state = previous;
      }
    },

    // #region transforms
    translate(x: number, y: number): void {
      mutable.state.transform = multiply(mutable.state.transform, [
        1,
        0,
        0,
        1,
        x,
        y,
      ]);
    },
    scale(x: number, y: number): void {
      mutable.state.transform = multiply(mutable.state.transform, [
        x,
        0,
        0,
        y,
        0,
        0,
      ]);
    },
    rotate(angle: number): void {
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      mutable.state.transform = multiply(mutable.state.transform, [
        cos,
        sin,
        -sin,
        cos,
        0,
        0,
      ]);
    },
    transform(
      a: number,
      b: number,
      c: number,
      d: number,
      e: number,
      f: number
    ): void {
      mutable.state.transform = multiply(mutable.state.transform, [
        a,
        b,
        c,
        d,
        e,
        f,
      ]);
    },
    setTransform(
      a: number,
      b: number,
      c: number,
      d: number,
      e: number,
      f: number
    ): void {
      mutable.state.transform = [a, b, c, d, e, f];
    },
    // #endregion

    setLineDash(segments: number[]): void {
      mutable.state.lineDash = [...segments];
    },
    getLineDash(): number[] {
      return [...mutable.state.lineDash];
    },

    /** bounding box of everything drawn so far, in root coordinates */
    getContentBounds(): {
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    } {
      return { ...bounds };
    },

    // #region path building
    beginPath(): void {
      mutable.path = '';
    },
    moveTo(x: number, y: number): void {
      mutable.path += `M${fmt(x)} ${fmt(y)}`;
      mutable.currentX = x;
      mutable.currentY = y;
      mark(x, y);
    },
    lineTo,
    quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void {
      mutable.path += `Q${fmt(cpx)} ${fmt(cpy)} ${fmt(x)} ${fmt(y)}`;
      mutable.currentX = x;
      mutable.currentY = y;
      mark(cpx, cpy);
      mark(x, y);
    },
    /**
     * canvas `arcTo`: line from the current point toward (x1,y1), then a circular
     * arc of `radius` tangent to segments (P0→P1) and (P1→P2), ending on P1→P2.
     */
    arcTo(
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      radius: number
    ): void {
      const x0 = mutable.currentX;
      const y0 = mutable.currentY;
      const dx0 = x0 - x1;
      const dy0 = y0 - y1;
      const dx2 = x2 - x1;
      const dy2 = y2 - y1;
      const len0 = Math.hypot(dx0, dy0);
      const len2 = Math.hypot(dx2, dy2);
      if (radius === 0 || len0 === 0 || len2 === 0) {
        lineTo(x1, y1);
        return;
      }
      const u0x = dx0 / len0;
      const u0y = dy0 / len0;
      const u2x = dx2 / len2;
      const u2y = dy2 / len2;
      const theta = Math.acos(Math.max(-1, Math.min(1, u0x * u2x + u0y * u2y)));
      if (theta === 0 || theta === Math.PI) {
        lineTo(x1, y1);
        return;
      }
      const tangent = radius / Math.tan(theta / 2);
      const t0x = x1 + u0x * tangent;
      const t0y = y1 + u0y * tangent;
      const t1x = x1 + u2x * tangent;
      const t1y = y1 + u2y * tangent;
      // turn direction: incoming (P0→P1) × outgoing (P1→P2); positive is clockwise in the y-down SVG space
      const cross = (x1 - x0) * (y2 - y1) - (y1 - y0) * (x2 - x1);
      const sweep = cross > 0 ? 1 : 0;
      mutable.path += `L${fmt(t0x)} ${fmt(t0y)}A${fmt(radius)} ${fmt(radius)} 0 0 ${sweep} ${fmt(t1x)} ${fmt(t1y)}`;
      mutable.currentX = t1x;
      mutable.currentY = t1y;
      mark(t0x, t0y);
      mark(t1x, t1y);
    },
    bezierCurveTo(
      cp1x: number,
      cp1y: number,
      cp2x: number,
      cp2y: number,
      x: number,
      y: number
    ): void {
      mutable.path += `C${fmt(cp1x)} ${fmt(cp1y)} ${fmt(cp2x)} ${fmt(cp2y)} ${fmt(x)} ${fmt(y)}`;
      mutable.currentX = x;
      mutable.currentY = y;
      mark(x, y);
    },
    closePath(): void {
      mutable.path += 'Z';
    },
    rect(x: number, y: number, w: number, h: number): void {
      mutable.path += `M${fmt(x)} ${fmt(y)}h${fmt(w)}v${fmt(h)}h${fmt(-w)}Z`;
      mark(x, y);
      mark(x + w, y + h);
    },
    roundRect(
      x: number,
      y: number,
      w: number,
      h: number,
      radii: number | number[] = 0
    ): void {
      const r = Math.min(
        typeof radii === 'number' ? radii : (radii[0] ?? 0),
        Math.abs(w) / 2,
        Math.abs(h) / 2
      );
      mutable.path +=
        `M${fmt(x + r)} ${fmt(y)}` +
        `h${fmt(w - 2 * r)}` +
        `a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(r)} ${fmt(r)}` +
        `v${fmt(h - 2 * r)}` +
        `a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(-r)} ${fmt(r)}` +
        `h${fmt(-(w - 2 * r))}` +
        `a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(-r)} ${fmt(-r)}` +
        `v${fmt(-(h - 2 * r))}` +
        `a${fmt(r)} ${fmt(r)} 0 0 1 ${fmt(r)} ${fmt(-r)}` +
        'Z';
      mark(x, y);
      mark(x + w, y + h);
    },
    arc(
      cx: number,
      cy: number,
      r: number,
      startAngle: number,
      endAngle: number,
      counterclockwise = false
    ): void {
      mutable.path += ellipseToPath(
        cx,
        cy,
        r,
        r,
        0,
        startAngle,
        endAngle,
        counterclockwise
      );
      mark(cx - r, cy - r);
      mark(cx + r, cy + r);
    },
    ellipse(
      cx: number,
      cy: number,
      rx: number,
      ry: number,
      rotation: number,
      startAngle: number,
      endAngle: number,
      counterclockwise = false
    ): void {
      mutable.path += ellipseToPath(
        cx,
        cy,
        rx,
        ry,
        rotation,
        startAngle,
        endAngle,
        counterclockwise
      );
      const rMax = Math.max(rx, ry);
      mark(cx - rMax, cy - rMax);
      mark(cx + rMax, cy + rMax);
    },
    // #endregion

    fill(): void {
      if (!mutable.path) return;
      elements.push(
        `<path d="${mutable.path}" fill="${escapeXmlAttr(mutable.state.fillStyle)}" ${commonAttrs()}/>`
      );
    },
    stroke(): void {
      if (!mutable.path) return;
      elements.push(
        `<path d="${mutable.path}" fill="none" ${strokeAttrs()} ${commonAttrs()}/>`
      );
    },
    fillRect(x: number, y: number, w: number, h: number): void {
      mark(x, y);
      mark(x + w, y + h);
      elements.push(
        `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(w)}" height="${fmt(h)}" fill="${escapeXmlAttr(mutable.state.fillStyle)}" ${commonAttrs()}/>`
      );
    },
    strokeRect(x: number, y: number, w: number, h: number): void {
      mark(x, y);
      mark(x + w, y + h);
      elements.push(
        `<rect x="${fmt(x)}" y="${fmt(y)}" width="${fmt(w)}" height="${fmt(h)}" fill="none" ${strokeAttrs()} ${commonAttrs()}/>`
      );
    },
    clearRect(): void {
      // no-op: SVG starts transparent; the export composites a background separately
    },

    measureText,
    fillText,
    strokeText(text: string, x: number, y: number): void {
      // approximation: emitted as filled <text> using fillStyle, not stroked; the
      // block diagram draws labels with fillText so this path is unused today.
      fillText(text, x, y);
    },

    drawImage(image: CanvasImageSource, ...coords: number[]): void {
      if (image instanceof HTMLImageElement) {
        drawnImages.add(image);
      }
      const href =
        image instanceof HTMLImageElement
          ? image.src
          : image instanceof HTMLCanvasElement
            ? image.toDataURL()
            : '';
      if (!href) return;
      if (coords.length === 8) {
        logWarn(
          new Error(
            'drawImage source-rectangle (9-arg) form is not supported; the source crop is ignored in SVG export'
          )
        );
      }
      const [dx, dy, dw, dh] = coords.length === 8 ? coords.slice(4) : coords;
      const width =
        dw ?? (image instanceof HTMLImageElement ? image.naturalWidth : 0);
      const height =
        dh ?? (image instanceof HTMLImageElement ? image.naturalHeight : 0);
      mark(dx, dy);
      mark(dx + width, dy + height);
      const def = defineImage(href, width, height);
      const sizeAttrs =
        def.kind === 'symbol'
          ? ` width="${fmt(width)}" height="${fmt(height)}"`
          : '';
      elements.push(
        `<use href="#${def.defId}" x="${fmt(dx)}" y="${fmt(dy)}"${sizeAttrs} ${commonAttrs()}/>`
      );
    },

    clip(): void {
      // intentionally not reproduced: applying an SVG clip-path (used for circular
      // component images) to the drawn elements prevented them from rendering.
      // component images therefore export unclipped (square) rather than not at all.
    },

    /** the recorded SVG: `<defs>` (extra defs + clip paths) followed by the drawn elements. */
    serialize(extraDefs = ''): string {
      const defsContent = `${extraDefs}${defs.join('')}`;
      const defsBlock = defsContent ? `<defs>${defsContent}</defs>` : '';
      return `${defsBlock}${elements.join('')}`;
    },
  };
};

export type CanvasToSvgContext = ReturnType<typeof createCanvasToSvgContext>;

/**
 * Creates a {@link CanvasToSvgContext} exposed as a `CanvasRenderingContext2D`.
 *
 * yFiles' dev-mode runtime type checks reject a plain object for the `ctx`
 * parameter of `HtmlCanvasVisual.render`, so we hand it a `Proxy` over a real
 * 2D context (which passes `instanceof CanvasRenderingContext2D`) and route the
 * canvas API to the recorder. Anything the recorder doesn't implement falls
 * through to the real (throwaway) context instead of throwing.
 */
/**
 * assert a recorder as the canvas context it emulates. The two are structurally
 * unrelated, so the cast goes via `object` (a legal downcast) to keep the
 * emulation boundary in one named place rather than scattering `as` casts.
 */
const asRenderingContext = (recorder: object): CanvasRenderingContext2D =>
  recorder as CanvasRenderingContext2D;

export const createRecordingContext = (): {
  context: CanvasRenderingContext2D;
  recorder: CanvasToSvgContext;
} => {
  const recorder = createCanvasToSvgContext();
  const realContext = document.createElement('canvas').getContext('2d');
  if (!realContext) {
    return { context: asRenderingContext(recorder), recorder };
  }

  const usesRecorder = (prop: string | symbol): boolean =>
    typeof prop === 'string' && Object.hasOwn(recorder, prop);

  const context = new Proxy(realContext, {
    get(target, prop) {
      const source = usesRecorder(prop) ? recorder : target;
      const value = Reflect.get(source, prop);
      return typeof value === 'function' ? value.bind(source) : value;
    },
    set(target, prop, value) {
      Reflect.set(usesRecorder(prop) ? recorder : target, prop, value);
      return true;
    },
  });

  return { context, recorder };
};
